<template>
  <v-container fluid class="cashout-page pa-6">
    <!-- Header -->
    <div
      class="d-flex align-center justify-space-between mb-5 flex-wrap"
      style="gap: 12px"
    >
      <div>
        <h1 class="page-title">E-Wallet Cashouts</h1>
        <p class="page-subtitle">
          Manage and process pending withdrawal requests
        </p>
      </div>
      <v-btn depressed class="refresh-btn" :loading="loading" @click="getData">
        <v-icon left size="16">mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </div>

    <!-- Stats -->
    <v-row class="mb-5">
      <v-col cols="12" sm="4">
        <div class="stat-card">
          <v-icon size="20" color="#F59E0B">mdi-clock-outline</v-icon>
          <div>
            <div class="stat-value">{{ inProgressCount }}</div>
            <div class="stat-label">In Progress</div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card">
          <v-icon size="20" color="#6366F1">mdi-format-list-bulleted</v-icon>
          <div>
            <div class="stat-value">{{ cashout.length }}</div>
            <div class="stat-label">Total Records</div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card">
          <v-icon size="20" color="#10B981">mdi-currency-usd</v-icon>
          <div>
            <div class="stat-value">RM{{ totalAmount }}</div>
            <div class="stat-label">Total Amount</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card class="table-card" elevation="0">
      <div class="table-card__header">
        <span class="table-card__title">Cashout Requests</span>
        <v-text-field
          v-model="search"
          placeholder="Search..."
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-magnify"
          style="max-width: 220px"
        />
      </div>

      <v-data-table
        :headers="headers"
        :items="cashout"
        :loading="loading"
        :search="search"
        :items-per-page="10"
        class="cashout-table"
      >
        <!-- Phone -->
        <template v-slot:item.phoneNumber="{ item }">
          <div class="d-flex align-center" style="gap: 6px">
            <span>{{ formatPhone(item.phoneNumber) }}</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  @click="copyPhone(item.phoneNumber)"
                >
                  <v-icon size="13" color="#9CA3AF">mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>Copy</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Amount -->
        <template v-slot:item.amount="{ item }">
          <span class="amount-value"
            >RM{{ Number(item.amount).toFixed(2) }}</span
          >
        </template>

        <!-- Date -->
        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <!-- Status -->
        <template v-slot:item.status="{ item }">
          <v-chip
            small
            :color="item.status === 'IN PROGRESS' ? 'orange' : 'green'"
            outlined
          >
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            depressed
            small
            color="success"
            :loading="item.loading"
            :disabled="item.status !== 'IN PROGRESS'"
            @click="markAsPaid(item)"
          >
            <v-icon left size="14">mdi-check</v-icon>
            Mark Paid
          </v-btn>
        </template>

        <!-- Empty -->
        <template v-slot:no-data>
          <div class="py-10 text-center">
            <v-icon size="36" color="#D1D5DB">mdi-check-all</v-icon>
            <div class="mt-2" style="color: #6b7280; font-size: 14px">
              No pending cashouts
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      bottom
      timeout="2500"
      rounded="pill"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2";
const service = new RestResource();

export default {
  data() {
    return {
      cashout: [],
      loading: false,
      search: "",
      snackbar: { show: false, text: "", color: "" },
      headers: [
        { text: "Email", value: "email" },
        { text: "Amount", value: "amount" },
        { text: "Vendor", value: "vendor" },
        { text: "Phone", value: "phoneNumber" },
        { text: "Created At", value: "createdAt" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    inProgressCount() {
      return this.cashout.filter((c) => c.status === "IN PROGRESS").length;
    },
    totalAmount() {
      return this.cashout
        .reduce((sum, c) => sum + Number(c.amount || 0), 0)
        .toFixed(2);
    },
  },

  mounted() {
    this.getData();
  },

  methods: {
    async getData() {
      this.loading = true;
      try {
        const r = await service.getCashoutData({
          status: "IN PROGRESS",
          cashoutType: "EWALLET",
        });
        this.cashout = r.data.map((item) => ({ ...item, loading: false }));
      } catch {
        this.showSnack("Failed to load data", "error");
      } finally {
        this.loading = false;
      }
    },

    async markAsPaid(item) {
      const dataToSend = {
        userId: item.userId,
        recordId: item._id,
        points: item.pointsRedeemed,
        phoneNumber: item.phoneNumber,
        vendor: item.vendor,
      };
      try {
        item.loading = true;
        await service.updatePaymentStatus(dataToSend);
        this.showSnack("Payment marked successfully", "success");
        await this.getData();
      } catch {
        this.showSnack("Failed to update payment", "error");
      } finally {
        item.loading = false;
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleString();
    },

    copyPhone(phone) {
      navigator.clipboard.writeText(phone);
      this.showSnack("Phone number copied", "info");
    },

    formatPhone(phone) {
      return phone ? phone.replace(/(\d{3})(\d{3})(\d+)/, "$1 $2 $3") : "—";
    },

    showSnack(text, color) {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.cashout-page {
  background: #f8fafc;
  min-height: 100vh;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.page-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 3px 0 0;
}

.refresh-btn {
  background: #fff !important;
  border: 1px solid #e5e7eb !important;
  color: #374151 !important;
  text-transform: none !important;
  font-weight: 500;
  border-radius: 8px !important;
  letter-spacing: 0 !important;
}

.stat-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}
.stat-label {
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
}

.table-card {
  border: 1px solid #e5e7eb !important;
  border-radius: 10px !important;
  overflow: hidden;
}
.table-card__header {
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff;
}
.table-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.amount-value {
  font-weight: 600;
  color: #111827;
}
</style>
